import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { hysteriFetch, wrappers } from 'hysterics';
import { sanitizeListingConfigId } from '../utils/submitSearchUtils';

const nlsFetchWrapper = hysteriFetch(
	'ws-inv-text-search-nls-suggestions-fetch'
);

/**
 * Custom hook to handle NLS fallback when FreeFormSearchService returns no facetInfo
 *
 * This hook will:
 * 1. Check if input has at least 2 words
 * 2. Check if there is no facetInfo from the primary API
 * 3. Wait for the configured timeout duration
 * 4. Make an NLS request to get fallback suggestions
 *
 * @param {string} input - The search input string
 * @param {Object} primaryResponse - The response from the primary search API
 * @param {number} timeout - The timeout in milliseconds before making NLS request
 * @returns {Object} - The NLS suggestions response or null
 */
const useNLSFallback = (input, primaryResponse, timeout) => {
	const [nlsResponse, setNlsResponse] = useState(null);
	const timeoutRef = useRef(null);
	const requestCounterRef = useRef(0);
	const { siteId } = useSelector((state) => state.widgetInfo);
	const { locale, deviceType, pageAlias, windowId, widgetName } = useSelector(
		(state) => state.requestData
	);

	useEffect(() => {
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		// Reset NLS response when input changes
		setNlsResponse(null);

		// Check conditions for NLS fallback:
		// 1. Input exists
		// 2. Input has at least 2 words
		// 3. Primary response exists but has no facetInfo
		const hasMultipleWords = input
			? /\b\w+\b.*\b\w+\b/.test(input.trim())
			: false;
		const hasPrimaryResponse =
			primaryResponse !== null && primaryResponse !== undefined;

		const hasFacetInfo =
			hasPrimaryResponse &&
			primaryResponse.suggestions &&
			primaryResponse.suggestions.length > 0 &&
			primaryResponse.suggestions[0].facetInfo &&
			Object.keys(primaryResponse.suggestions[0].facetInfo).length > 0;

		if (input && hasMultipleWords && !hasFacetInfo && timeout > 0) {
			// Increment request counter and capture it for this request
			requestCounterRef.current += 1;
			const currentRequestId = requestCounterRef.current;

			// Set timeout to make NLS request
			timeoutRef.current = setTimeout(async () => {
				try {
					// Get listing config from DDC.InvData prefs
					const listingConfigIdPref =
						window.DDC?.InvData?.prefs?.['listing.config.id'] ||
						'auto-new,auto-used';

					const listingConfigId =
						sanitizeListingConfigId(listingConfigIdPref);

					const facetInstanceId =
						window.DDC?.InvData?.prefs?.facetInstanceId ||
						'listing';

					// Build URL for existing nlInventorySearch endpoint
					const queryParams = new URLSearchParams({
						q: input
					});

					const nlsUrl = `/api/widget/ws-inv-text-search/nlInventorySearch/${siteId}/${locale}?${queryParams.toString()}`;

					const nlsResult = await nlsFetchWrapper(
						nlsUrl,
						{
							method: 'POST',
							headers: {
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({
								device: deviceType,
								pageAlias,
								windowId,
								widgetName,
								listingConfigId,
								facetInstanceId
							})
						},
						{
							[wrappers.timeout.contextKey]: {
								timeout: 15000
							},
							[wrappers.retry.contextKey]: {
								retries: 0
							}
						}
					);

					// Only set the NLS response if we got valid suggestions AND this is still the latest request
					if (
						nlsResult &&
						nlsResult.suggestions &&
						nlsResult.suggestions.length > 0
					) {
						if (currentRequestId === requestCounterRef.current) {
							setNlsResponse(nlsResult);
						}
					}
				} catch (error) {
					// Error fetching NLS fallback suggestions
					// Only clear NLS response if this is still the latest request
					if (currentRequestId === requestCounterRef.current) {
						setNlsResponse(null);
					}
				}
			}, timeout);
		}

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [
		input,
		primaryResponse,
		timeout,
		siteId,
		locale,
		deviceType,
		pageAlias,
		windowId,
		widgetName
	]);

	return nlsResponse;
};

export default useNLSFallback;
